import type { Bible, TimeSelection } from "./types";
import type { WorldData } from "./world";
import {
  characterKnows,
  eventsAtMoment,
  eventsVisibleAt,
  getCharacter,
  getMoment,
  isRevealedToReader,
  isSecretObjectivelyTrue,
  relationshipsAt,
  resolveCharacterState,
  secretsForView,
} from "./engine";

export type ContextRequest = {
  momentId: string;
  characterIds: string[];
  arcIds?: string[];
  includeAuthorSecrets: boolean;
  includeOpenIssues: boolean;
  world?: WorldData | null;
};

export function generateWritingContext(
  bible: Bible,
  selection: TimeSelection,
  req: ContextRequest,
): string {
  const moment = getMoment(bible, req.momentId);
  if (!moment) return "Moment introuvable. Aucun contexte généré.";

  const book = bible.books.find((b) => b.id === moment.bookId);
  const branch = bible.branches.find((b) => b.id === moment.branchId);
  const lines: string[] = [];

  lines.push(`# Contexte d’écriture — ${moment.label}`);
  lines.push("");
  lines.push(`- Tome : ${book?.number ?? "?"} · ${book?.title ?? moment.bookId}`);
  lines.push(`- Chapitre : ${moment.chapterNumber}${moment.title ? ` — ${moment.title}` : ""}`);
  lines.push(`- Branche : ${branch?.name ?? moment.branchId} (${moment.branchId})`);
  if (moment.loopIterationId) lines.push(`- Itération : ${moment.loopIterationId}`);
  lines.push(`- Ordre de lecture : ${moment.readingOrder}`);
  lines.push(`- Ordre interne : ${moment.storyOrder ?? "inconnu"}`);
  lines.push("");

  const atMoment = eventsAtMoment(bible, moment.id);
  const recent = eventsVisibleAt(bible, moment.id)
    .filter((e) => e.momentId !== moment.id)
    .slice(-5);
  lines.push("## Événements immédiats");
  if (atMoment.length === 0 && recent.length === 0) {
    lines.push("Aucun événement structuré pour ce moment.");
  }
  for (const event of [...recent, ...atMoment]) {
    lines.push(`- [${event.status}] ${event.title} (${event.momentId}) : ${event.summary}`);
    if (event.consequences?.length) {
      lines.push(`  Conséquences : ${event.consequences.join(" ; ")}`);
    }
  }
  lines.push("");

  lines.push("## Personnages");
  const ids = req.characterIds.length ? req.characterIds : [];
  for (const id of ids) {
    const character = getCharacter(bible, id);
    if (!character) continue;
    const state = resolveCharacterState(bible, id, moment.id);
    const tone = req.world?.tones?.[id];
    lines.push(`### ${character.name}`);
    lines.push(`- Classe : ${state?.className ?? character.className}`);
    lines.push(`- Don (vérité de fiche) : ${character.gift}`);
    lines.push(`- Localisation : ${state?.location ?? "non renseignée à cet instant"}`);
    lines.push(`- État physique : ${state?.physicalState ?? "non renseigné"}`);
    lines.push(`- État psychologique : ${state?.psychologicalState ?? "non renseigné"}`);
    lines.push(`- Statut de vie : ${state?.lifeStatus ?? "alive"}`);
    if (state?.rank != null) lines.push(`- Classement atteint : ${state.rank}e`);
    if (state?.guild) lines.push(`- Guilde : ${state.guild}`);
    if (state?.immediateGoal) lines.push(`- Objectif immédiat : ${state.immediateGoal}`);
    if (tone) lines.push(`- Ton à préserver : ${tone}`);
    const rels = relationshipsAt(bible, moment.id, id);
    if (rels.length) {
      lines.push("- Relations actives :");
      for (const rel of rels) {
        const otherId = rel.fromCharacterId === id ? rel.toCharacterId : rel.fromCharacterId;
        const other = getCharacter(bible, otherId);
        lines.push(`  - ${rel.label} (${rel.type}) avec ${other?.name ?? otherId} — ${rel.summary ?? ""}`);
      }
    }
    lines.push("- Ce que ce personnage sait :");
    let knew = false;
    for (const secret of bible.secrets) {
      const knowledge = characterKnows(bible, secret, id, moment.id);
      if (knowledge) {
        knew = true;
        lines.push(
          `  - ${secret.title} [${knowledge.certainty}] depuis ${knowledge.knownFromMomentId}${knowledge.believedVersion ? ` — croit : ${knowledge.believedVersion}` : ""}`,
        );
      }
    }
    if (!knew) lines.push("  - Rien de structuré au-delà de la fiche.");
    lines.push("");
  }

  lines.push("## Ce que le lecteur sait");
  for (const secret of bible.secrets) {
    const reader = isRevealedToReader(bible, secret, moment.id);
    const trueNow = isSecretObjectivelyTrue(bible, secret, moment.id);
    if (reader) {
      lines.push(`- Révélé : ${secret.title}`);
    } else if (trueNow) {
      lines.push(`- Encore caché au lecteur : ${secret.title}`);
    }
  }
  lines.push("");

  if (req.includeAuthorSecrets) {
    lines.push("## Secrets d’auteur (ne pas verser tels quels dans le chapitre)");
    for (const row of secretsForView(bible, moment.id, "author")) {
      if (row.hiddenFromReader) {
        lines.push(`- ${row.secret.title} : ${row.secret.objectiveTruth}`);
      }
    }
    lines.push("");
  }

  if (req.arcIds?.length && req.world?.arcs) {
    lines.push("## Arcs à garder en tête");
    for (const arc of req.world.arcs.filter((a) => req.arcIds!.includes(a.id))) {
      lines.push(`- ${arc.title} [${arc.state}] — ${arc.summary}`);
    }
    lines.push("");
  }

  if (req.includeOpenIssues) {
    const open = bible.continuityIssues.filter(
      (i) => i.state === "open" || i.state === "decision-needed" || i.state === "investigating",
    );
    lines.push("## Contradictions ouvertes pertinentes");
    if (!open.length) lines.push("Aucune contradiction ouverte dans le registre.");
    for (const issue of open) {
      const touches =
        !issue.momentIds?.length ||
        issue.momentIds.includes(moment.id) ||
        issue.entityRefs.some((ref) => ids.includes(ref));
      if (!touches && issue.momentIds?.length) continue;
      lines.push(`- [${issue.severity}] ${issue.title} — ${issue.description}`);
    }
    lines.push("");
  }

  lines.push("## Consignes");
  lines.push("- N’invente aucune action future ni aucun fait absent de ce contexte.");
  lines.push("- Ne fusionne pas les itérations temporelles.");
  lines.push("- Ne modèle pas un personnage mort dans une itération comme simplement « revenu » dans une autre.");
  if (selection.viewer === "reader") {
    lines.push("- Vue lecteur : ne révèle pas les secrets encore cachés.");
  }
  lines.push("");
  lines.push(`Sources de ce moment : ${(moment.sources || []).map((s) => s.label).join(" ; ") || "non renseignées"}`);

  return lines.join("\n");
}
